/**
 * AuditLinearView — tabela linear de eventos (A5).
 *
 * Colunas: Quando · Tipo · Ator · Entidade · Severidade. Só os campos REAIS
 * do serializer (sem opId/summary — não serializados). Clique numa linha abre
 * o detalhe. Eventos com payload mascarado ganham um cadeado discreto; os
 * metadados permanecem visíveis (o backend só mascara o payload).
 */

import { SEVERITY_BADGE, SEVERITY_LABEL, formatTimestamp, isPayloadMasked } from './audit-labels';

import type { AuditEventWire } from './types';

interface Props {
  events: AuditEventWire[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  onSelect: (event: AuditEventWire) => void;
}

export function AuditLinearView({
  events,
  isLoading,
  isError,
  hasMore,
  onLoadMore,
  isLoadingMore,
  onSelect,
}: Props): JSX.Element {
  if (isError) {
    return (
      <p role="alert" className="px-8 py-6 text-sm text-red-600">
        Não foi possível carregar os eventos de auditoria.
      </p>
    );
  }

  if (isLoading) {
    return <p className="px-8 py-6 text-sm text-text-secondary">Carregando eventos…</p>;
  }

  if (events.length === 0) {
    return (
      <p className="px-8 py-10 text-center text-sm text-text-secondary">
        Nenhum evento de auditoria para os filtros selecionados.
      </p>
    );
  }

  return (
    <div className="px-8 py-4">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-border-subtle text-left text-text-secondary">
            <th className="py-2 pr-3 font-medium">Quando</th>
            <th className="py-2 pr-3 font-medium">Tipo</th>
            <th className="py-2 pr-3 font-medium">Ator</th>
            <th className="py-2 pr-3 font-medium">Entidade</th>
            <th className="py-2 pr-3 font-medium">Severidade</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => {
            const masked = isPayloadMasked(ev);
            return (
              <tr
                key={ev.id}
                onClick={() => onSelect(ev)}
                className="cursor-pointer border-b border-border-subtle/60 hover:bg-surface-raised"
              >
                <td className="py-2 pr-3 align-top text-text-secondary">
                  {formatTimestamp(ev.occurredAt)}
                </td>
                <td className="py-2 pr-3 align-top">
                  <span className="font-medium text-text-primary">{ev.eventType}</span>
                  {masked && (
                    <span
                      title="Payload sensível mascarado — requer audit-events-read-sensitive"
                      className="ml-1.5 text-text-secondary"
                      aria-label="payload mascarado"
                    >
                      🔒
                    </span>
                  )}
                </td>
                <td
                  className="py-2 pr-3 align-top text-text-secondary"
                  title={ev.actorUserId ?? undefined}
                >
                  {/* nome real do wire; degrada para o id (dado forense), nunca fabrica */}
                  {ev.actorName ?? (
                    <span className="font-mono">
                      {ev.actorUserId ?? <span className="italic">sistema</span>}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 align-top text-text-secondary">
                  <span className="font-medium text-text-primary">{ev.entityType}</span>
                  <span className="ml-1 font-mono">{ev.entityId}</span>
                </td>
                <td className="py-2 pr-3 align-top">
                  <span
                    className={`rounded-full px-1.5 py-0.5 font-medium ${SEVERITY_BADGE[ev.severity]}`}
                  >
                    {SEVERITY_LABEL[ev.severity]}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasMore && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="rounded-md border border-border-subtle px-4 py-1.5 text-xs text-text-secondary hover:bg-surface-raised disabled:opacity-50"
          >
            {isLoadingMore ? 'Carregando…' : 'Carregar mais'}
          </button>
        </div>
      )}
    </div>
  );
}
